import { Descriptions } from 'antd';
import { T, TF } from 'react-translator-component';

import { formatDateTime, formatKwh, formatMoney, formatPower, maskTag, SESSION_STATUS, statusOf } from './ocpiHelpers';
import type { Navigate } from './OcpiPage';
import StatusTag from './StatusTag';
import * as S from './styled';

import { OcpiLocation, OcpiSession } from '../../../types/responses/Ocpi';

interface Props {
  session: OcpiSession;
  location?: OcpiLocation;
  hasCdr: boolean;
  nav: Navigate;
}

const TIMELINE_TEXTS = {
  started: 'Started',
  endedEmpty: 'Ended without consumption',
  noCdr: 'No CDR',
  noCdrWhy: 'Invalid charges do not generate a CDR',
  waiting: 'Waiting to start',
  ended: 'Ended',
  cdr: 'CDR',
  cdrLater: 'Generated when it ends',
  inProgress: 'In progress',
  cdrAvailable: 'CDR available',
};

type Step = [keyof typeof TIMELINE_TEXTS, string];

/** Linha do tempo a partir do que o CS devolve: início, fim, energia e a existência do CDR. */
const timeline = (session: OcpiSession, hasCdr: boolean) => {
  const start = formatDateTime(session.startTimestamp);
  const stop = formatDateTime(session.stopTimestamp);
  const later = TF(TIMELINE_TEXTS.cdrLater);
  switch (session.status) {
    case 'INVALID':
      return { current: 1, status: 'error' as const, steps: [['started', start], ['endedEmpty', stop], ['noCdr', TF(TIMELINE_TEXTS.noCdrWhy)]] as Step[] };
    case 'PENDING':
      return { current: 0, status: 'process' as const, steps: [['waiting', start], ['ended', '—'], ['cdr', later]] as Step[] };
    case 'ACTIVE':
      return { current: 1, status: 'process' as const, steps: [['started', start], ['inProgress', formatKwh(session.kwh)], ['cdr', later]] as Step[] };
    default:
      return {
        current: 2,
        status: 'finish' as const,
        steps: [['started', start], ['ended', stop], hasCdr ? ['cdrAvailable', formatMoney(session.income, session.currency)] : ['cdr', '—']] as Step[],
      };
  }
};

/** done: etapa concluída; current: onde a recarga está; error: onde falhou; todo: ainda não chegou. */
const stepState = (index: number, current: number, status: 'error' | 'process' | 'finish'): string => {
  if (index < current || (status === 'finish' && index === current)) return 'done';
  if (index === current) return status === 'error' ? 'error' : 'current';
  return 'todo';
};

const SessionDetail = ({ session, location, hasCdr, nav }: Props) => {
  const connector = location?.connectors?.find((c) => c.connectorId === session.connectorId);
  const flow = timeline(session, hasCdr);

  return (
    <>
      <S.Panel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontSize: 20, margin: 0 }}>
            {T('Charge')} #{session.transactionPk}
          </h2>
          <StatusTag {...statusOf(SESSION_STATUS, session.status)} />
        </div>
        <Descriptions size="small" column={4} layout="vertical">
          <Descriptions.Item label={T('Station')}>{location?.chargeBoxId || session.chargeBoxUuid || '—'}</Descriptions.Item>
          <Descriptions.Item label={T('Connector')}>
            {[session.connectorId, connector?.connectorType, connector?.powerMax && formatPower(connector.powerMax)]
              .filter(Boolean)
              .join(' · ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label={T('Tag')}>
            <S.Mono>{maskTag(session.idTag)}</S.Mono>
          </Descriptions.Item>
          <Descriptions.Item label={T('Currency')}>{session.currency || '—'}</Descriptions.Item>
          <Descriptions.Item label={T('Start')}>{formatDateTime(session.startTimestamp)}</Descriptions.Item>
          <Descriptions.Item label={T('End')}>{formatDateTime(session.stopTimestamp)}</Descriptions.Item>
          <Descriptions.Item label={T('Energy')}>{formatKwh(session.kwh)}</Descriptions.Item>
          <Descriptions.Item label={T('Amount')}>{formatMoney(session.income, session.currency)}</Descriptions.Item>
        </Descriptions>
      </S.Panel>

      <S.Panel>
        <h2>{T('Timeline')}</h2>
        <S.Timeline>
          {flow.steps.map(([key, description], index) => (
            <li key={key} className={stepState(index, flow.current, flow.status)}>
              <span className="dot" />
              <strong>{T(TIMELINE_TEXTS[key])}</strong>
              <S.Muted>{description}</S.Muted>
            </li>
          ))}
        </S.Timeline>
      </S.Panel>

      <S.Panel style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
        {location?.chargeBoxUuid && (
          <S.LinkButton type="button" onClick={() => nav.open({ kind: 'station', uuid: location.chargeBoxUuid as string })}>
            {T('View station')} {location.chargeBoxId}
          </S.LinkButton>
        )}
        {hasCdr && (
          <S.LinkButton type="button" onClick={() => nav.open({ kind: 'cdr', pk: session.transactionPk })}>
            {T('View CDR')}
          </S.LinkButton>
        )}
      </S.Panel>
    </>
  );
};

export default SessionDetail;
